import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, CloseButton, Drawer, HStack, Input, Portal, Text, VStack, chakra } from '@chakra-ui/react'
import { canvasserApi } from '../../../lib/api'
import { serverMessage } from '../../../lib/syncEngine'
import type { OffListInfo } from '../../../lib/doorDraft'
import type { HouseholdLookupContactDto } from '../../../types/canvasser'

export interface OffListPerson { contactId: string; name: string; offList: OffListInfo }
type Mode = 'choose' | 'lookup' | 'add'
const MEMBER: { value: 'Yes' | 'No' | 'NotSure'; label: string }[] = [
  { value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }, { value: 'NotSure', label: 'Not sure' },
]
const orNull = (s: string) => (s.trim() ? s.trim() : null)
const MIN_SEARCH = 2

/**
 * [Spec §4.5, §15.1 D5, P4-S5] "Someone not on my list", anchored on THIS stop: the server only discloses
 * people at the same address/worksite and only creates the person here. Union tier also captures
 * Member? / phone / email / context, stored on the VISIT (B1), never on the contact's union data.
 * Online-only (P4-5). [R15] The lookup runs once when the lookup step opens; the name filter is sent only
 * on Search / Enter with >= 2 characters (the endpoint is rate-limited and PII-audited).
 */
export function OffListSheet({ open, onClose, variant, turfId, anchorTurfMemberId, unionTier, isOnline, onTheyMovedOut, onAdded }: {
  open: boolean; onClose: () => void; variant: 'home' | 'worksite'; turfId: string; anchorTurfMemberId: string
  unionTier: boolean; isOnline: boolean; onTheyMovedOut: () => void; onAdded: (p: OffListPerson) => void
}) {
  const [mode, setMode] = useState<Mode>(variant === 'home' ? 'choose' : 'lookup')
  const [q, setQ] = useState('')
  const [matches, setMatches] = useState<HouseholdLookupContactDto[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [member, setMember] = useState<'Yes' | 'No' | 'NotSure' | null>(null)
  const [context, setContext] = useState('')
  // Set once createDoorContact succeeds, keyed by the fields it was created from: a retry after a failed
  // addToTurf reuses it (no second Contact); editing name/phone/email makes the key differ, which discards it.
  const [created, setCreated] = useState<{ key: string; contactId: string } | null>(null)
  const seq = useRef(0)

  // Fresh sheet on every open: reset during render when `open` flips true (same pattern as NotAtWorkSheet).
  const [wasOpen, setWasOpen] = useState(open)
  if (wasOpen !== open) {
    setWasOpen(open)
    if (open) {
      setMode(variant === 'home' ? 'choose' : 'lookup'); setQ(''); setMatches(null); setBusy(false); setError(null)
      setFirst(''); setLast(''); setPhone(''); setEmail(''); setMember(null); setContext(''); setCreated(null)
    }
  }

  const runLookup = useCallback((name?: string) => {
    const mine = ++seq.current
    setError(null)
    canvasserApi.householdLookup(turfId, anchorTurfMemberId, name)
      .then(r => { if (mine === seq.current) setMatches(r.data) })
      .catch(err => { if (mine === seq.current) setError(serverMessage(err, "Couldn't look that up")) })
  }, [turfId, anchorTurfMemberId])

  // One unfiltered lookup when the lookup step opens (never per keystroke).
  useEffect(() => {
    if (!open || mode !== 'lookup' || !isOnline) return
    runLookup(undefined)
    return () => { seq.current++ }
  }, [open, mode, isOnline, runLookup])

  const canSearch = q.trim().length >= MIN_SEARCH
  const search = () => { if (canSearch) runLookup(q.trim()) }

  const union = (kind: OffListInfo['kind']): OffListInfo => ({
    kind,
    memberAnswer: unionTier ? member : null,
    phone: unionTier ? orNull(phone) : null,
    email: unionTier ? orNull(email) : null,
    context: unionTier ? orNull(context) : null,
  })

  const addExisting = async (m: HouseholdLookupContactDto) => {
    if (busy) return
    setBusy(true); setError(null)
    try {
      await canvasserApi.addToTurf({ turfId, contactId: m.contactId, anchorTurfMemberId })
      onAdded({ contactId: m.contactId, name: `${m.firstName} ${m.lastName}`.trim(), offList: union('ExistingContact') })
    } catch (err) { setError(serverMessage(err, "Couldn't add them")) } finally { setBusy(false) }
  }

  const addNew = async () => {
    if (busy || !first.trim() || !last.trim()) return
    setBusy(true); setError(null)
    try {
      const body = {
        anchorTurfMemberId, firstName: first.trim(), lastName: last.trim(),
        phone: unionTier ? orNull(phone) : null, email: unionTier ? orNull(email) : null,
      }
      const key = JSON.stringify(body)
      let contactId = created?.key === key ? created.contactId : null
      if (!contactId) {
        contactId = (await canvasserApi.createDoorContact(turfId, body)).data.contactId
        setCreated({ key, contactId })
      }
      await canvasserApi.addToTurf({ turfId, contactId, anchorTurfMemberId })
      onAdded({ contactId, name: `${first.trim()} ${last.trim()}`, offList: union('NewContact') })
    } catch (err) { setError(serverMessage(err, "Couldn't add them")) } finally { setBusy(false) }
  }

  const unionFields = unionTier && (
    <VStack align="stretch" gap={2}>
      <Text fontWeight="semibold">Member?</Text>
      <HStack gap={2} role="radiogroup" aria-label="Member?">
        {MEMBER.map(m => (
          <chakra.label key={m.value} display="flex" alignItems="center" gap={2} minH="44px" px={3} borderWidth="1px"
            borderRadius="full" borderColor={member === m.value ? 'accent' : 'border'} bg={member === m.value ? 'accent.soft' : 'surface'}>
            <input type="radio" name="offlist-member" checked={member === m.value} onChange={() => setMember(m.value)} />
            <Text fontSize="sm">{m.label}</Text>
          </chakra.label>
        ))}
      </HStack>
      <Input aria-label="Phone (optional)" placeholder="Phone (optional)" minH="44px" maxLength={32} inputMode="tel"
        value={phone} onChange={(e) => setPhone(e.target.value)} />
      <Input aria-label="Email (optional)" placeholder="Email (optional)" minH="44px" maxLength={254} inputMode="email"
        value={email} onChange={(e) => setEmail(e.target.value)} />
      <Input aria-label="Context for your organizer" placeholder="Context for your organizer" minH="44px" maxLength={1000}
        value={context} onChange={(e) => setContext(e.target.value)} />
    </VStack>
  )

  return (
    <Drawer.Root open={open} placement="bottom" onOpenChange={(e) => { if (!e.open) onClose() }}>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content borderTopRadius="xl" maxH="85vh" pb="env(safe-area-inset-bottom, 0px)" shadow="popover">
            <Drawer.CloseTrigger asChild><CloseButton size="sm" minW="44px" minH="44px" /></Drawer.CloseTrigger>
            <Drawer.Header><Drawer.Title>Someone not on my list</Drawer.Title></Drawer.Header>
            <Drawer.Body>
              <VStack align="stretch" gap={3} pb={4}>
                {mode === 'choose' && (
                  <>
                    <Button minH="52px" variant="outline" borderColor="border" onClick={onTheyMovedOut}>They moved out</Button>
                    {isOnline
                      ? <Button minH="52px" bg="accent" color="accent.fg" onClick={() => setMode('lookup')}>Talk to them anyway</Button>
                      : <Text color="fg.muted">Adding someone needs a connection.</Text>}
                  </>
                )}
                {mode !== 'choose' && !isOnline && <Text color="fg.muted">Adding someone needs a connection.</Text>}
                {mode === 'lookup' && isOnline && (
                  <>
                    <HStack gap={2}>
                      <Input aria-label="Name" placeholder="Name" minH="44px" maxLength={100} value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search() } }} />
                      <Button minH="44px" variant="outline" borderColor="border" disabled={!canSearch} onClick={search}>Search</Button>
                    </HStack>
                    {(matches ?? []).map(m => (
                      <Button key={m.contactId} minH="52px" justifyContent="flex-start" variant="outline" borderColor="border"
                        disabled={busy} onClick={() => addExisting(m)}>
                        {`${m.firstName} ${m.lastName}`.trim()}
                      </Button>
                    ))}
                    {unionFields}
                    <Button minH="48px" variant="ghost" onClick={() => { setError(null); setMode('add') }}>Not listed? Add them</Button>
                  </>
                )}
                {mode === 'add' && isOnline && (
                  <>
                    <Input aria-label="First name" placeholder="First name" minH="44px" maxLength={100} value={first} onChange={(e) => setFirst(e.target.value)} />
                    <Input aria-label="Last name" placeholder="Last name" minH="44px" maxLength={100} value={last} onChange={(e) => setLast(e.target.value)} />
                    {unionFields}
                    <Button minH="52px" bg="accent" color="accent.fg" disabled={busy || !first.trim() || !last.trim()} onClick={addNew}>Add</Button>
                  </>
                )}
                {error && <Text color="danger.fg">{error}</Text>}
              </VStack>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
